/**
 * Service for managing search history business logic.
 * Handles adding, filtering, deduplication, and size limits.
 */

import { SearchHistoryItem, SearchHistoryConfig } from './types';
import { getHistory, saveHistory } from './searchHistoryStorage';

const DEFAULT_CONFIG: Required<SearchHistoryConfig> = {
  maxItems: 10,
  storageKey: 'search.history',
  minQueryLength: 2,
};

/**
 * Get search history with optional configuration.
 */
export function getSearchHistory(config?: SearchHistoryConfig): SearchHistoryItem[] {
  const { storageKey } = { ...DEFAULT_CONFIG, ...config };
  return getHistory(storageKey);
}

/**
 * Add a query to search history.
 * Follows rules:
 * - Query is trimmed
 * - Minimum length check (default: 2 chars)
 * - No duplicates (if exists, move to beginning)
 * - Max items limit (default: 10)
 * - Newest items first
 *
 * Returns true if query was added/saved, false otherwise.
 */
export function addToHistory(query: string, config?: SearchHistoryConfig): boolean {
  const { maxItems, storageKey, minQueryLength } = { ...DEFAULT_CONFIG, ...config };

  const trimmedQuery = query.trim();

  // Check minimum length
  if (trimmedQuery.length < minQueryLength) {
    return false;
  }

  // Get current history
  const currentHistory = getHistory(storageKey);

  // Find existing item with same query (case-insensitive)
  const existingIndex = currentHistory.findIndex(
    item => item.query.toLowerCase() === trimmedQuery.toLowerCase()
  );

  const newItem: SearchHistoryItem = {
    query: trimmedQuery,
    timestamp: Date.now(),
  };

  let newHistory: SearchHistoryItem[];

  if (existingIndex >= 0) {
    // Move existing item to beginning (update timestamp)
    newHistory = [
      newItem, // Use new item with current timestamp
      ...currentHistory.slice(0, existingIndex),
      ...currentHistory.slice(existingIndex + 1)
    ];
  } else {
    // Add new item at beginning
    newHistory = [newItem, ...currentHistory];
  }

  // Enforce max items limit
  if (newHistory.length > maxItems) {
    newHistory = newHistory.slice(0, maxItems);
  }

  // Save to storage
  return saveHistory(newHistory, storageKey);
}

/**
 * Filter history by current query input.
 * Case-insensitive match using includes or startsWith.
 * Returns filtered array (newest first).
 */
export function filterHistoryByQuery(
  query: string,
  config?: SearchHistoryConfig
): SearchHistoryItem[] {
  const history = getSearchHistory(config);
  const trimmedQuery = query.trim().toLowerCase();

  if (!trimmedQuery) {
    return history; // Return all history when query is empty
  }

  return history.filter(item =>
    item.query.toLowerCase().includes(trimmedQuery)
  );
}

/**
 * Clear all search history.
 */
export function clearSearchHistory(config?: SearchHistoryConfig): boolean {
  const { storageKey } = { ...DEFAULT_CONFIG, ...config };
  return saveHistory([], storageKey);
}

/**
 * Remove a specific query from history.
 * Returns true if removed, false otherwise.
 */
export function removeFromHistory(query: string, config?: SearchHistoryConfig): boolean {
  const { storageKey } = { ...DEFAULT_CONFIG, ...config };
  const history = getHistory(storageKey);

  const filteredHistory = history.filter(
    item => item.query.toLowerCase() !== query.trim().toLowerCase()
  );

  if (filteredHistory.length === history.length) {
    return false; // No change
  }

  return saveHistory(filteredHistory, storageKey);
}

/**
 * Get history size (number of items).
 */
export function getHistorySize(config?: SearchHistoryConfig): number {
  return getSearchHistory(config).length;
}

/**
 * Check if a query exists in history.
 */
export function hasQueryInHistory(query: string, config?: SearchHistoryConfig): boolean {
  const history = getSearchHistory(config);
  const trimmedQuery = query.trim().toLowerCase();

  return history.some(item => item.query.toLowerCase() === trimmedQuery);
}